'use client'

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { Send } from 'lucide-react'
import { useSupport } from '@/context/SupportContext'
import { isAuthenticated } from '@/lib/auth'
import { useLoginModal } from '@/context/LoginModalContext'
import { SupportMessageMarkdown } from '@/app/components/support/SupportMessageMarkdown'
import { SUPPORT_AI_ENABLED } from '@/lib/config'

function formatTime(iso: string): string {
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

const panelShell =
  'font-montserrat flex w-full flex-col overflow-hidden rounded-[24px] border border-[#c5c1fa]/60 bg-[#F3F4F6] text-[#183b56] shadow-[5px_20px_21.7px_rgba(97,85,245,0.16),0px_4px_4px_rgba(97,85,245,0.24)] min-h-0'

// Pages whose MainLayout renders a mobile bottom nav the panel must clear (mirrors SupportFab).
const BOTTOM_NAV_PATHS = ['/dashboard', '/history', '/welcome']
function hasMobileBottomNav(pathname: string | null): boolean {
  if (!pathname) return false
  return BOTTOM_NAV_PATHS.some(p => pathname === p || pathname.startsWith(p + '/'))
}

const SUPPORT_CHAT_MAX_CHARS = 200
const TEXTAREA_MAX_HEIGHT_PX = 120
const TEXTAREA_MIN_HEIGHT_PX = 44

export function SupportPanelLegacy() {
  const pathname = usePathname()
  // The FAB is hidden while the panel is open, so the panel anchors flush to the bottom (where the
  // FAB was, 20px) instead of reserving the FAB's space. On pages with a mobile bottom-nav bar,
  // keep enough clearance so the panel isn't hidden behind it.
  const bottomNav = hasMobileBottomNav(pathname)
  const { openLoginModal } = useLoginModal()
  const {
    isOpen,
    closePanel,
    session,
    pendingUserMessage,
    loading,
    actionLoading,
    error,
    isTyping,
    selectOption,
    sendChatMessage,
    endChat,
    clearError,
    newChat,
    activeThreadId,
    activeWriteToken,
  } = useSupport()

  const openLoginFromSupport = useCallback(() => {
    try {
      if (activeThreadId) {
        sessionStorage.setItem(
          'support_panel_restore',
          JSON.stringify({ threadId: activeThreadId, writeToken: activeWriteToken ?? null }),
        )
      }
    } catch { }
    openLoginModal(undefined, 'support_typed_chat')
  }, [activeThreadId, activeWriteToken, openLoginModal])
  const panelRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const shouldInitialFocusRef = useRef(true)
  const [showAllOptions, setShowAllOptions] = useState(false)
  const [chatDraft, setChatDraft] = useState('')

  const isLoggedIn = isAuthenticated()
  const visibleOptions = session?.contextOptions ?? session?.options ?? []
  const hasOptions = visibleOptions.length > 0
  const showChatComposer =
    session != null && !session.closed && !loading && isLoggedIn && SUPPORT_AI_ENABLED
  const showGuestTypeHint =
    session != null && !session.closed && !loading && !isLoggedIn && SUPPORT_AI_ENABLED
  const isConversationClosed = Boolean(session?.closed && !loading && !isTyping)
  const showQuickReplies = hasOptions && !loading && !isTyping
  const displayedMessages =
    session == null
      ? []
      : pendingUserMessage &&
        !session.messages.some(
          (m) =>
            m.role === pendingUserMessage.role &&
            m.text === pendingUserMessage.text &&
            m.createdAt === pendingUserMessage.createdAt,
        )
        ? [...session.messages, pendingUserMessage]
        : session.messages

  const tryClosePanel = useCallback(() => {
    if (chatDraft.trim()) {
      if (!window.confirm('Discard your unsent message?')) return
      setChatDraft('')
    }
    closePanel()
  }, [chatDraft, closePanel])

  const adjustTextareaHeight = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    const next = Math.min(
      Math.max(el.scrollHeight, TEXTAREA_MIN_HEIGHT_PX),
      TEXTAREA_MAX_HEIGHT_PX,
    )
    el.style.height = `${next}px`
  }, [])

  useEffect(() => {
    setShowAllOptions(false)
  }, [session?.currentNodeId])

  useLayoutEffect(() => {
    if (!isOpen) return
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [
    isOpen,
    session?.messages,
    pendingUserMessage,
    isTyping,
    loading,
    isConversationClosed,
    session?.options,
    session?.contextOptions,
  ])

  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      tryClosePanel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, tryClosePanel])

  useEffect(() => {
    if (!isOpen) return
    return () => {
      queueMicrotask(() => {
        document.querySelector<HTMLElement>('[data-support-fab-trigger]')?.focus()
      })
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen) {
      shouldInitialFocusRef.current = true
    }
  }, [isOpen, session?.threadId])

  useLayoutEffect(() => {
    if (!isOpen || !showChatComposer) return
    adjustTextareaHeight()
    if (!shouldInitialFocusRef.current) return

    const hasOpts = hasOptions && !loading && !isTyping
    if (hasOpts && panelRef.current) {
      panelRef.current
        .querySelector<HTMLButtonElement>('[data-support-option]')
        ?.focus()
    } else {
      textareaRef.current?.focus()
    }
    shouldInitialFocusRef.current = false
  }, [
    isOpen,
    showChatComposer,
    session?.threadId,
    hasOptions,
    loading,
    isTyping,
    adjustTextareaHeight,
  ])

  useEffect(() => {
    if (!isOpen || !panelRef.current) return

    const root = panelRef.current
    const selector =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

    const focusables = () =>
      Array.from(root.querySelectorAll<HTMLElement>(selector))

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return
      const list = focusables()
      if (list.length === 0) return
      const first = list[0]
      const last = list[list.length - 1]
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault()
          last.focus()
        }
      } else if (document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [
    isOpen,
    session?.messages,
    showQuickReplies,
    showChatComposer,
    showGuestTypeHint,
    error,
  ])

  if (!isOpen) return null

  const canRestart =
    session &&
    (session.messages.length > 1 ||
      (session.messages[0] && session.messages[0].role === 'user'))

  const showEndChatAction = Boolean(session && canRestart && !session.closed)
  const usedChars = Array.from(chatDraft).length
  const remainingChars = Math.max(0, SUPPORT_CHAT_MAX_CHARS - usedChars)
  const isNearCharLimit = remainingChars <= 20

  const submitMessage = () => {
    const t = chatDraft.trim()
    if (!t || usedChars > SUPPORT_CHAT_MAX_CHARS || actionLoading || isTyping) return
    setChatDraft('')
    void sendChatMessage(t)
    requestAnimationFrame(() => adjustTextareaHeight())
  }

  return (
    <div
      className={`font-montserrat fixed inset-0 z-[70] flex items-end justify-end ${bottomNav ? 'max-md:pb-[calc(76px+env(safe-area-inset-bottom,0px))]' : 'max-md:pb-[max(20px,env(safe-area-inset-bottom,0px))]'} max-md:pr-[max(20px,env(safe-area-inset-right,0px))] md:p-0 pointer-events-none`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="support-panel-title"
    >
      <div
        role="presentation"
        className="pointer-events-auto absolute inset-0 cursor-default bg-[#183b56]/10 max-md:bg-[#183b56]/12"
        onClick={() => tryClosePanel()}
      />
      <div
        ref={panelRef}
        className={`pointer-events-auto relative z-10 max-md:w-[min(calc(100vw-2.5rem),345px)] max-md:h-[min(85dvh,669px)] md:fixed md:bottom-[max(2rem,env(safe-area-inset-bottom))] md:right-[max(2rem,env(safe-area-inset-right))] md:m-0 md:h-[669px] md:max-h-[669px] md:w-[390px] ${panelShell}`}
      >
        <header className="relative shrink-0 overflow-visible border-b border-[#ece8f4] bg-white px-5 py-4 rounded-t-[24px]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-full">
                <Image
                  src="/images/support.png"
                  alt="Mira"
                  width={40}
                  height={40}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="flex items-baseline gap-2">
                <h2
                  id="support-panel-title"
                  className="font-montserrat text-xl font-bold leading-tight text-[#1F153E]"
                >
                  Mira
                </h2>
                <span className="font-montserrat text-sm font-medium text-[#6A678E]">
                  Here to help
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {showEndChatAction ? (
                <button
                  type="button"
                  onClick={() => void endChat()}
                  disabled={actionLoading}
                  className="inline-flex h-8 min-h-8 shrink-0 items-center justify-center rounded-full border border-[#e2e6ea] bg-[#f2f4f6] px-3 font-montserrat text-xs font-semibold text-[#183b56] transition hover:bg-[#e6e9ed]"
                >
                  End chat
                </button>
              ) : null}
              <button
                type="button"
                onClick={tryClosePanel}
                aria-label="Close support panel"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#6A678E] hover:bg-[#f3f4f6] hover:text-[#183b56] transition"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          </div>
        </header>

        {error && (
          <div className="shrink-0 border-b border-red-200/80 bg-rose-50/95 px-3 py-2.5 font-montserrat text-xs font-medium text-red-900">
            {error}
            <button
              type="button"
              onClick={clearError}
              className="ml-2 text-red-800 underline decoration-red-400 underline-offset-2"
            >
              Dismiss
            </button>
          </div>
        )}

        {!isLoggedIn && (
          <div className="shrink-0 border-b border-amber-200/80 bg-amber-50/95 px-3 py-2 text-[11px] leading-relaxed text-amber-950 hidden">
            <p>
              <button
                type="button"
                className="font-montserrat font-semibold text-amber-900 underline decoration-amber-600/50 underline-offset-2"
                onClick={() => openLoginFromSupport()}
              >
                Log in
              </button>{' '}
              to keep history in your account and unlock member-only help.
            </p>
          </div>
        )}

        <div className="flex min-h-0 flex-1 flex-col bg-[#F3F4F6]">
          {loading && !session ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8">
              <div
                className="h-8 w-8 animate-spin rounded-full border-2 border-[#e4dff7] border-t-[#6155f5]"
                aria-hidden
              />
              <p className="font-montserrat text-sm font-medium text-[#5a7184]">
                Connecting you to help…
              </p>
            </div>
          ) : !session && !actionLoading ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-6 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl">
                <Image
                  src="/images/support.png"
                  alt=""
                  width={48}
                  height={48}
                  className="h-full w-full object-contain"
                  aria-hidden
                />
              </div>
              <p className="max-w-xs font-montserrat text-sm font-medium leading-relaxed text-[#5a7184]">
                Your support conversation will appear here in a moment.
              </p>
            </div>
          ) : !session && actionLoading ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8">
              <div
                className="h-8 w-8 animate-spin rounded-full border-2 border-[#e4dff7] border-t-[#6155f5]"
                aria-hidden
              />
              <p className="font-montserrat text-sm font-medium text-[#5a7184]">
                Starting support…
              </p>
            </div>
          ) : session != null ? (
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-4">
                {displayedMessages.length > 0 ? (
                  <div className="mb-4 flex items-center gap-3 px-1" aria-hidden>
                    <div className="h-px flex-1 bg-[#e4e0f5]" />
                    <span className="shrink-0 font-montserrat text-xs font-medium text-[#a0a8b4]">
                      Today
                    </span>
                    <div className="h-px flex-1 bg-[#e4e0f5]" />
                  </div>
                ) : null}
                {displayedMessages.map((m, i) => (
                  <div
                    key={`${m.role}-${i}-${m.createdAt}`}
                    className={`mb-4 flex items-start gap-3 font-montserrat ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    {m.role === 'user' ? null : (
                      <Image
                        src="/images/support.png"
                        alt="Mira"
                        width={36}
                        height={36}
                        className="h-9 w-9 shrink-0 rounded-full object-cover"
                        aria-hidden
                      />
                    )}
                    <div
                      className={
                        m.role === 'user'
                          ? 'max-w-[85%] rounded-2xl rounded-br-sm bg-gradient-to-br from-[#6155f5] to-[#453CAE] px-4 py-3 text-left text-[14.5px] font-medium leading-relaxed text-white shadow-sm'
                          : 'max-w-[85%] rounded-[20px] rounded-tl-sm bg-[#f4f3fe] border border-[#dddafc] px-4 py-3.5 text-left text-[14.5px] font-medium leading-relaxed text-[#1B1943] shadow-sm'
                      }
                    >
                      <SupportMessageMarkdown
                        content={m.text}
                        variant={m.role === 'user' ? 'user' : 'bot'}
                      />
                      {m.createdAt ? (
                        <p
                          className={
                            m.role === 'user'
                              ? 'mt-1.5 text-right text-[10px] font-medium text-white/80'
                              : 'mt-1.5 text-right text-[10px] font-medium text-[#8B87A8]'
                          }
                        >
                          {formatTime(m.createdAt)}
                        </p>
                      ) : null}
                    </div>
                  </div>
                ))}

                {isTyping && (
                  <div className="mb-3 flex justify-start" aria-live="polite">
                    <div className="inline-flex max-w-[90%] items-center gap-2 rounded-2xl rounded-bl-md border border-[#e4dff7] bg-white px-3.5 py-2.5 text-sm font-medium text-[#5a7184] shadow-sm">
                      <span className="flex gap-0.5">
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#6155f5] [animation-delay:0ms]" />
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#6155f5] [animation-delay:150ms]" />
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#6155f5] [animation-delay:300ms]" />
                      </span>
                      <span>Typing…</span>
                    </div>
                  </div>
                )}

                <div ref={bottomRef} />
              </div>

              {isConversationClosed && (
                <div className="flex shrink-0 flex-col gap-3 border-t border-[#e4e0f5]/80 bg-[#F3F4F6] px-5 py-3">
                  <p className="text-center font-montserrat text-sm font-medium leading-relaxed text-[#5a7184]">
                    The conversation has been closed.
                  </p>
                  <button
                    type="button"
                    onClick={() => void newChat()}
                    disabled={actionLoading}
                    aria-busy={actionLoading}
                    className="inline-flex min-h-11 w-full items-center justify-center rounded-full bg-gradient-to-r from-[#6155f5] to-[#453CAE] px-4 py-2.5 font-montserrat text-sm font-bold text-white shadow-sm transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Start new conversation
                  </button>
                </div>
              )}

              {!isConversationClosed &&
                (showQuickReplies || showChatComposer || showGuestTypeHint) ? (
                <div className="shrink-0 bg-[#F3F4F6]">
                  {showQuickReplies ? (
                    <div className="px-5 pt-2 pb-3">
                      {isLoggedIn ? (
                        <p
                          id="support-suggested-label"
                          className="mb-2 font-montserrat text-xs font-medium text-[#5a7184]"
                        >
                          Suggested replies
                        </p>
                      ) : null}
                      <div
                        className="flex min-w-0 max-w-full flex-col gap-3"
                        role="group"
                        aria-labelledby="support-suggested-label"
                      >
                        {(showAllOptions
                          ? visibleOptions
                          : visibleOptions
                        ).map((o) => (
                          <button
                            key={o.id}
                            type="button"
                            data-support-option
                            disabled={actionLoading}
                            onClick={() => void selectOption(o.id)}
                            className="inline-flex min-h-[46px] w-full min-w-0 items-center justify-center rounded-full bg-gradient-to-r from-[#6155f5] to-[#453CAE] px-5 py-3 text-center font-lato text-[15px] font-bold leading-snug text-white shadow-md transition hover:brightness-105 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {o.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {showGuestTypeHint ? (
                    <div className="px-4 pb-4 pt-1">
                      <div className="rounded-[20px] bg-white px-4 py-3.5 text-center font-inter text-[13.5px] leading-relaxed text-[#6A678E] shadow-sm border border-[#ede9f5]">
                        <button
                          type="button"
                          className="font-bold text-[#6155f5] underline decoration-[#6155f5]/40 underline-offset-2 hover:decoration-[#6155f5]"
                          onClick={() => openLoginFromSupport()}
                        >
                          Log in
                        </button>{' '}
                        to type a question and get smart help.
                      </div>
                    </div>
                  ) : null}

                  {showChatComposer ? (
                    <div
                      className={`px-3 ${showQuickReplies ? 'pb-3 pt-1' : 'py-3'}`}
                    >
                      <form
                        className="flex flex-col gap-1.5"
                        onSubmit={(e) => {
                          e.preventDefault()
                          submitMessage()
                        }}
                      >
                        <div className="flex gap-2 items-end">
                          <label
                            htmlFor="support-chat-input"
                            className="sr-only"
                          >
                            Ask a question
                          </label>
                          <textarea
                            ref={textareaRef}
                            id="support-chat-input"
                            rows={1}
                            maxLength={SUPPORT_CHAT_MAX_CHARS}
                            value={chatDraft}
                            onChange={(e) => {
                              const next = Array.from(e.target.value)
                                .slice(0, SUPPORT_CHAT_MAX_CHARS)
                                .join('')
                              setChatDraft(next)
                              requestAnimationFrame(() =>
                                adjustTextareaHeight(),
                              )
                            }}
                            onKeyDown={(e) => {
                              if (e.key !== 'Enter' || e.shiftKey) return
                              e.preventDefault()
                              submitMessage()
                            }}
                            placeholder="Ask a question..."
                            disabled={actionLoading || isTyping}
                            className="min-h-[2.75rem] max-h-[7.5rem] flex-1 resize-none rounded-xl border border-[#e2e6ea] bg-[#fafbfc] px-3 py-2.5 font-montserrat text-sm leading-relaxed text-[#183b56] outline-none ring-[#6155f5]/20 placeholder:text-[#94a3b8] focus:border-[#6155f5]/40 focus:ring-2 disabled:opacity-50"
                            style={{ height: TEXTAREA_MIN_HEIGHT_PX }}
                          />
                          <button
                            type="submit"
                            disabled={
                              actionLoading ||
                              isTyping ||
                              !chatDraft.trim() ||
                              usedChars > SUPPORT_CHAT_MAX_CHARS
                            }
                            className="inline-flex h-10 w-10 shrink-0 items-center justify-center self-end rounded-xl bg-gradient-to-br from-[#6155f5] to-[#453CAE] font-montserrat text-white shadow-sm transition hover:from-[#5346e0] hover:to-[#3b3299] disabled:cursor-not-allowed disabled:opacity-40"
                            aria-label="Send message"
                          >
                            <Send className="size-5" strokeWidth={2.25} />
                          </button>
                        </div>
                        <p
                          className={`text-right text-[11px] font-medium ${isNearCharLimit ? 'text-amber-700' : 'text-[#5a7184]'}`}
                        >
                          {usedChars}/{SUPPORT_CHAT_MAX_CHARS}
                        </p>
                      </form>
                    </div>
                  ) : null}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
